import {createContext, useContext, useEffect, useState} from "react"
import {useAppKitAccount} from "@reown/appkit/react"
import {useAppKitConnection} from "@reown/appkit-adapter-solana/react"
import {getAccount, getAssociatedTokenAddress} from "@solana/spl-token"
import {PublicKey} from "@solana/web3.js"
import BN from "bn.js"
import {
  TOKEN_USDC_MINT_ADDRESS,
  TOKEN_ZNX_MINT_ADDRESS,
} from "@/common/const/constants.ts"

type BalanceContextType = {
  balance: string | null;
  loading: boolean;
  balanceUsdc: string | null;
  loadingUsdc: boolean;
};

const BalanceContext = createContext<BalanceContextType>({
  balance: null,
  loading: false,
  balanceUsdc: null,
  loadingUsdc: false,
})

export const BalanceProvider = ({children}: { children: React.ReactNode }) => {
  const [balance, setBalance] = useState<string | null>(null)
  const [balanceUsdc, setBalanceUsdc] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [loadingUsdc, setLoadingUsdc] = useState(false)
  const {address, isConnected} = useAppKitAccount()
  const {connection} = useAppKitConnection()
  const tokenZnxMintAddress = TOKEN_ZNX_MINT_ADDRESS
  const tokenUsdcMintAddress = TOKEN_USDC_MINT_ADDRESS

  function tokenBalanceToUiNumber(amount: BN | number | string): number {
    if (typeof amount === "string") {
      return parseFloat(amount)
    } else if (typeof amount === "number") {
      return amount
    }
    return amount.divn(1e6).toNumber()
  }

  useEffect(() => {
    const fetchBalance = async () => {
      if (!isConnected || !address || !connection) return
      setLoading(true)

      try {
        const tokenMint = new PublicKey(tokenZnxMintAddress)
        const walletPubkey = new PublicKey(address)
        const tokenAccount = await getAssociatedTokenAddress(tokenMint, walletPubkey)
        const accountInfo = await getAccount(connection, tokenAccount)
        const raw = new BN(accountInfo.amount)
        const converted = tokenBalanceToUiNumber(raw)
        setBalance(converted.toString())
      } catch (err) {

        if (err == 'TokenAccountNotFoundError') {
          setBalance('0')
          return
        }
        console.error("Failed to fetch balance", err)
        setBalance(null)
      } finally {
        setLoading(false)
      }
    }
    const fetchBalanceUsdc = async () => {
      if (!isConnected || !address || !connection) return
      setLoadingUsdc(true)

      try {
        const tokenMint = new PublicKey(tokenUsdcMintAddress)
        const walletPubkey = new PublicKey(address)
        const tokenAccount = await getAssociatedTokenAddress(tokenMint, walletPubkey)
        const accountInfo = await getAccount(connection, tokenAccount)
        const raw = new BN(accountInfo.amount)
        const converted = tokenBalanceToUiNumber(raw)
        setBalanceUsdc(converted.toString())
      } catch (err) {

        if (err == 'TokenAccountNotFoundError') {
          setBalanceUsdc('0')
          return
        }
        console.error("Failed to fetch balance", err)
        setBalanceUsdc(null)
      } finally {
        setLoadingUsdc(false)
      }
    }


    fetchBalance()
    fetchBalanceUsdc()
  }, [isConnected, address, connection])

  return (
    <BalanceContext.Provider value={{balance, loading, balanceUsdc, loadingUsdc}}>
      {children}
    </BalanceContext.Provider>
  )
}

export const useBalance = () => useContext(BalanceContext)
